import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Box, Skeleton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useCustomColors } from "../hooks/useCustomColors";
import { goldShineSx } from "../utils/goldShine";

interface WalletBalanceCardProps {
  balance: number | null;
  name?: string;
  onClick?: () => void;
}

const formatBalance = (balance: number) =>
  new Intl.NumberFormat("en-MY", {
    style: "currency",
    currency: "MYR",
    minimumFractionDigits: 2,
  }).format(balance);

const WalletBalanceCard = ({ balance, name, onClick }: WalletBalanceCardProps) => {
  const colors = useCustomColors();
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";
  const { t } = useTranslation();
  const isLoading = balance === null;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        isolation: "isolate",
        "&::after": {
          content: '""',
          position: "absolute",
          left: "10%",
          right: "10%",
          bottom: "-14px",
          height: "30px",
          borderRadius: "50%",
          background: "rgba(184, 147, 43, 0.32)",
          filter: "blur(18px)",
          opacity: 0.72,
          zIndex: 0,
          pointerEvents: "none",
          animation: "wallet-backlight-breathe 3.8s ease-in-out infinite",
          "@keyframes wallet-backlight-breathe": {
            "0%, 100%": { opacity: 0.48, transform: "scaleX(0.88)" },
            "50%": { opacity: 0.82, transform: "scaleX(1.08)" },
          },
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: "146px", md: "152px" },
          p: "1px",
          borderRadius: "24px",
          overflow: "hidden",
          background: "transparent",
          zIndex: 1,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            background: "conic-gradient(from 0deg, #6f5313, #f0c94d, #7d5d16, #b8932b, #4e3a10, #f0c94d, #6f5313)",
            animation: "wallet-border-spin 8s linear infinite",
            "@keyframes wallet-border-spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            p: "19px",
            textAlign: "left",
            color: colors.text.primary,
            borderRadius: "23px",
            background: colors.gradient.wallet,
            boxShadow: `${colors.glow.cardEdge}, ${colors.glow.card}`,
          }}
        >
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 800,
            letterSpacing: "2px",
            color: isDark ? "rgba(255, 255, 255, 0.78)" : colors.text.secondary,
          }}
        >
          {t("wallet_balance")}
        </Typography>
        {isLoading ? (
          <Skeleton
            variant="text"
            width="150px"
            sx={{ mt: "4px", bgcolor: isDark ? "rgba(255,255,255,.22)" : colors.background.deep }}
          />
        ) : (
          <Typography
            sx={{
              mt: "4px",
              fontSize: { xs: "24px", md: "26px" },
              fontWeight: 900,
              lineHeight: 1.15,
              ...goldShineSx(colors),
            }}
          >
            {formatBalance(balance ?? 0)}
          </Typography>
        )}
        <Typography
          noWrap
          sx={{
            mt: "auto",
            maxWidth: "45%",
            fontSize: "12px",
            fontWeight: 700,
            color: isDark ? "rgba(255, 255, 255, 0.82)" : colors.text.primary,
          }}
        >
          {name || "—"}
        </Typography>
        </Box>
      </Box>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        sx={{
          position: "absolute",
          right: "8px",
          bottom: "-14px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          height: "44px",
          pl: "5px",
          pr: "14px",
          border: "1px solid rgba(122, 90, 20, 0.85)",
          borderRadius: "999px",
          background: colors.gradient.goldPill,
          color: colors.background.field,
          cursor: onClick ? "pointer" : "default",
          boxShadow: colors.glow.topUpRing,
          zIndex: 2,
          overflow: "hidden",
          isolation: "isolate",
          transition:
            "box-shadow 180ms ease, border-color 180ms ease, transform 180ms ease",
          // Metallic gold rim: a gradient ring masked to the 1.5px edge only.
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            padding: "3px",
            background: colors.gradient.goldMetal,
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            pointerEvents: "none",
          },
          // Shine sweep across the rim only.
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
            padding: "2px",
            background:
              "linear-gradient(110deg, transparent 40%, rgba(255, 252, 232, 0.95) 50%, transparent 60%)",
            backgroundSize: "260% 100%",
            WebkitMask:
              "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
            animation: "topUpRimShine 4.2s ease-in-out infinite",
            pointerEvents: "none",
          },
          "@keyframes topUpRimShine": {
            "0%": { backgroundPosition: "170% 0" },
            "55%, 100%": { backgroundPosition: "-70% 0" },
          },
          "&:hover": {
            borderColor: "rgba(232, 200, 90, 0.88)",
            boxShadow: colors.glow.topUpRingHover,
            transform: "scale(1.01)",
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "&::after": { animation: "none", opacity: 0 },
            "&:hover": { transform: "none" },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "34px",
            height: "34px",
            flexShrink: 0,
            borderRadius: "50%",
            background: colors.gradient.redGem,
            boxShadow: [
              "inset 0 2px 4px rgba(255, 255, 255, 0.22)",
              "inset 0 -3px 8px rgba(0, 0, 0, 0.55)",
              "0 0 0 2px #C9A445",
              "0 0 0 3px rgba(74, 15, 12, 0.85)",
              "0 2px 4px rgba(0, 0, 0, 0.45)",
            ].join(", "),
          }}
        >
          <AddRoundedIcon sx={{ fontSize: "20px", color: colors.accent.goldBright }} />
        </Box>
        <Typography noWrap sx={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.4px", color: "#5A1416", textShadow: "0 1px 0 rgba(255, 246, 214, 0.75)", position: "relative", zIndex: 1 }}>
          {t("top_up_title")}
        </Typography>
      </Box>
    </Box>
  );
};

export default WalletBalanceCard;
